import { useEffect, useState } from 'react';
import { RotateCcw, RotateCw } from 'lucide-react';
import { bbox, moveObj, resizeObj } from './whiteboardShapes';
import { ROTATES_BY_FIELD, lineAngle, rotateObj, centerOf, normDeg } from './wbGeometry';
import { Btn } from './wbControls';

const r1 = (n) => Math.round(n * 10) / 10;

// Typed values apply on Enter / leaving the field, so half-typed numbers never squash the shape.
function NumField({ label, value, onChange, testid, min = -2000, max = 4000 }) {
  const [draft, setDraft] = useState(String(r1(value)));
  useEffect(() => { setDraft(String(r1(value))); }, [value]);
  const apply = () => {
    const v = Number(draft);
    if (draft.trim() !== '' && Number.isFinite(v) && r1(v) !== r1(value)) onChange(Math.max(min, Math.min(max, v)));
    else setDraft(String(r1(value)));
  };
  return (
    <label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
      <span className="w-5 text-right">{label}</span>
      <input type="number" inputMode="decimal" step="1" value={draft} data-testid={testid}
        onChange={(e) => setDraft(e.target.value)} onBlur={apply} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
        className="w-[4.5rem] h-8 px-1.5 border border-slate-200 rounded text-sm text-slate-800 normal-case tracking-normal" />
    </label>
  );
}

function LineFields({ o, onReplace }) {
  const mid = { x: (o.x1 + o.x2) / 2, y: (o.y1 + o.y2) / 2 };
  const len = Math.hypot(o.x2 - o.x1, o.y2 - o.y1);
  const setLen = (L) => {
    const a = Math.atan2(o.y2 - o.y1, o.x2 - o.x1), h = Math.max(1, L) / 2;
    onReplace({ ...o, x1: mid.x - h * Math.cos(a), y1: mid.y - h * Math.sin(a), x2: mid.x + h * Math.cos(a), y2: mid.y + h * Math.sin(a) });
  };
  return (
    <>
      <NumField label="X1" value={o.x1} onChange={(v) => onReplace({ ...o, x1: v })} testid="wb-geo-x1" />
      <NumField label="Y1" value={o.y1} onChange={(v) => onReplace({ ...o, y1: v })} testid="wb-geo-y1" />
      <NumField label="X2" value={o.x2} onChange={(v) => onReplace({ ...o, x2: v })} testid="wb-geo-x2" />
      <NumField label="Y2" value={o.y2} onChange={(v) => onReplace({ ...o, y2: v })} testid="wb-geo-y2" />
      <NumField label="Len" value={len} min={1} onChange={setLen} testid="wb-geo-length" />
      <NumField label="°" value={lineAngle(o)} min={-360} max={360} testid="wb-geo-angle"
        onChange={(v) => onReplace(rotateObj(o, v - lineAngle(o), mid.x, mid.y))} />
    </>
  );
}

function BoxFields({ o, onReplace }) {
  const b = bbox(o);
  const setBox = (patch) => onReplace(resizeObj(o, b, { ...b, ...patch }));
  return (
    <>
      <NumField label="X" value={b.x} onChange={(v) => onReplace(moveObj(o, v - b.x, 0))} testid="wb-geo-x" />
      <NumField label="Y" value={b.y} onChange={(v) => onReplace(moveObj(o, 0, v - b.y))} testid="wb-geo-y" />
      {o.type !== 'text' ? <NumField label="W" value={b.w} min={1} onChange={(v) => setBox({ w: v })} testid="wb-geo-w" /> : null}
      {o.type !== 'text' ? <NumField label="H" value={b.h} min={1} onChange={(v) => setBox({ h: v })} testid="wb-geo-h" /> : null}
      {ROTATES_BY_FIELD.has(o.type) ? (
        <NumField label="°" value={o.rotation || 0} min={-360} max={360} testid="wb-geo-angle"
          onChange={(v) => onReplace({ ...o, rotation: normDeg(v) || undefined })} />
      ) : null}
    </>
  );
}

// Precise position / size / angle for one object, plus 15° rotate buttons that also work for groups (touch-friendly).
export function GeometryPanel({ single, onReplace, onRotateBy }) {
  const isLine = single && (single.type === 'line' || single.type === 'arrow');
  return (
    <div data-testid="wb-geometry-panel" className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {single && isLine ? <LineFields o={single} onReplace={onReplace} /> : null}
      {single && !isLine ? <BoxFields o={single} onReplace={onReplace} /> : null}
      <div className="flex items-center gap-1">
        <Btn title="Rotate 15° left" onClick={() => onRotateBy(-15)} testid="wb-rotate-left"><RotateCcw className="h-4 w-4" />15°</Btn>
        <Btn title="Rotate 15° right" onClick={() => onRotateBy(15)} testid="wb-rotate-right"><RotateCw className="h-4 w-4" />15°</Btn>
      </div>
    </div>
  );
}

export const rotateSelection = (list, deg, center) => list.map((o) => {
  const c = center || centerOf(o);
  return rotateObj(o, deg, c.x, c.y);
});
